import { expandRecurringItem } from '../utils/expandRecurringItem';
import getItemIdToUpdate from '../utils/getItemIdToUpdate';
import { applyLocalLiePropertiesToSeriesEvents } from 'owa-calendar-event-local-lie';
import { addOrModifyItemInCache } from '../utils/SharedCacheFunctions';
import { ViewType } from 'owa-calendar-actions';
import { isException, isOccurrence, isRecurringMaster } from 'owa-calendar-event-capabilities';
import CalendarEvent, { CalendarItemTypeTypeEx } from 'owa-calendar-types/lib/types/CalendarEvent';
import { isBefore } from 'owa-datetime';
import EventScope from 'owa-service/lib/contract/EventScope';
import { orchestrator } from 'satcheljs';
import {
    isEventsCacheInitialized,
    getAllLockedDateRanges,
} from '../selectors/eventsCacheSelectors';
import {
    getCalendarEventWithId,
    filterCalendarEvents,
} from '../selectors/calendarFolderEventsSelectors';
import {
    addCalendarEventsWithinCurrentLockedDateRanges,
    removeCalendarEventsFromEventsCacheMatchingFilter,
    updateItem,
} from '../actions/eventsCacheActions';
import {
    instantCalendarEventUpdate,
    CalendarEventOperationResult,
    calendarEventUpdated,
} from '../actions/publicActions';
import { reloadItemsCache } from '../utils/SharedCacheFunctions';

orchestrator(instantCalendarEventUpdate, async actionMessage => {
    const {
        eventId,
        eventChanges,
        errorMessage,
        actionToExecute,
        onEventUpdateFailed,
        onEventUpdateSuccess,
        onEventUpdatedLocally,
        actionSource,
    } = actionMessage;

    let eventScope = actionMessage.eventScope;

    // We first check if the event is in our cache, if not then we use the one provided in the message
    let cachedEvent = getCalendarEventWithId(eventId.Id);

    let idToUpdate = getItemIdToUpdate(eventId.Id, actionMessage.eventScope);

    if (cachedEvent && isRecurringMaster(cachedEvent.CalendarItemType)) {
        eventScope = EventScope.AllInstancesInSeries;
    }

    let allowLocalUpdate = isInstantUpdateSupported(cachedEvent, eventChanges);
    if (allowLocalUpdate) {
        onUpdateItemStart(
            cachedEvent,
            eventChanges,
            eventScope,
            onEventUpdatedLocally,
            actionSource
        );
    }
    let retryFunction = () =>
        instantCalendarEventUpdate(
            eventId,
            eventChanges,
            eventScope,
            actionToExecute,
            onEventUpdateSuccess,
            () => onEventUpdateFailed(errorMessage, null, actionSource),
            actionSource,
            errorMessage
        );

    let result: CalendarEventOperationResult = null;
    try {
        result = await actionToExecute(idToUpdate);
    } catch (error) {
        // Exception, assume update failed
        if (allowLocalUpdate) {
            reloadItemsCache(cachedEvent.ParentFolderId.Id);
        }
        onEventUpdateFailed(error, retryFunction, actionSource);
        throw error;
    }

    if (!result.isSuccessful) {
        // Either response failed or we got no item back
        if (allowLocalUpdate) {
            reloadItemsCache(cachedEvent.ParentFolderId.Id);
        }
        onEventUpdateFailed(errorMessage, retryFunction, actionSource);
        throw errorMessage;
    }

    onEventUpdateSuccess();
});

function onUpdateItemStart(
    itemBeforeUpdate: CalendarEvent,
    itemChanges: CalendarEvent,
    eventScope: EventScope,
    onItemUpdatedLocally: (updatedEvent: CalendarEvent, actionSource: ViewType) => void,
    actionSource: ViewType
): void {
    // Create new item with all props from original item + changes
    const folderId = itemBeforeUpdate.ParentFolderId.Id;
    let itemForSave: CalendarEvent = { ...itemBeforeUpdate, ...itemChanges };

    if (
        eventScope == EventScope.AllInstancesInSeries ||
        eventScope == EventScope.ThisAndFollowingInstances
    ) {
        if (
            itemChanges.Recurrence ||
            itemChanges.Start ||
            itemChanges.StartTimeZoneId ||
            itemChanges.End ||
            itemChanges.EndTimeZoneId ||
            eventScope == EventScope.ThisAndFollowingInstances
        ) {
            let start = itemBeforeUpdate.Start;

            // if changing the recurrence pattern, remove all existing items and recreate them
            // remove both exceptions and occurrences because changing the recurrence pattern will overwrite all exceptions
            removeCalendarEventsFromEventsCacheMatchingFilter(folderId, item => {
                return (
                    isOccurrence(item.CalendarItemType) &&
                    item.InstanceKey == itemBeforeUpdate.InstanceKey &&
                    (eventScope == EventScope.AllInstancesInSeries ||
                        (eventScope == EventScope.ThisAndFollowingInstances &&
                            !isBefore(item.Start, start)))
                );
            });

            // Expand new pattern only on series update.
            // During "from this day forward" update we don't have a way to
            // match lies with new series.
            if (eventScope == EventScope.AllInstancesInSeries) {
                const allCachedDateRanges = getAllLockedDateRanges(folderId);
                let expandedItems = expandRecurringItem(itemForSave, allCachedDateRanges);
                applyLocalLiePropertiesToSeriesEvents(expandedItems.concat(itemForSave));
                let itemsWithLies = expandedItems.map(occurrence => ({
                    ...occurrence,
                    CalendarItemType: 'Occurrence' as CalendarItemTypeTypeEx,
                }));
                addCalendarEventsWithinCurrentLockedDateRanges(
                    itemForSave.ParentFolderId.Id,
                    itemsWithLies
                );
            }
        } else {
            let cachedOccurrences = filterCalendarEvents(folderId, (item: CalendarEvent) => {
                return (
                    item.InstanceKey == itemBeforeUpdate.InstanceKey &&
                    isOccurrence(item.CalendarItemType)
                );
            });

            // Update the cache entries directly
            for (let cachedOccurrence of cachedOccurrences) {
                let itemModel = cachedOccurrence;
                updateItem(itemModel, itemChanges);
                calendarEventUpdated(itemModel);
            }
        }
    }

    // If the caller wants to update the entire series from an exception, do not update the exception item
    // Series updates do not apply to exceptions
    if (
        !(
            isException(itemBeforeUpdate.CalendarItemType) &&
            eventScope == EventScope.AllInstancesInSeries
        )
    ) {
        // Handle when item changes from recurring to single, in which case we remove all old occurrences and then insert the new
        // single item
        let wasAlwaysSingleItem =
            itemBeforeUpdate.InstanceKey == null ||
            isOccurrence(itemBeforeUpdate.CalendarItemType) ||
            isException(itemBeforeUpdate.CalendarItemType);

        // Do not add to the cache if not present. TODO: except if it's an update after we saved an attachment on a new item
        addOrModifyItemInCache(itemForSave, wasAlwaysSingleItem, true /* only modify in cache */);
    }
    onItemUpdatedLocally(itemForSave, actionSource);
}

function isInstantUpdateSupported(itemBeforeUpdate: CalendarEvent, itemChanges: CalendarEvent) {
    // If there is no item to update in the cache, then no instant updates
    if (!itemBeforeUpdate) {
        return false;
    }

    // If there is no loaded cache for the item being updated, this means that we are adding
    let calendarId = itemBeforeUpdate.ParentFolderId.Id;
    if (!isEventsCacheInitialized(calendarId)) {
        return false;
    }

    // We do not support instant updates for changes across calendars
    let changedCalendarId = itemChanges.ParentFolderId;
    if (changedCalendarId && changedCalendarId.Id != calendarId) {
        return false;
    }

    return true;
}
